// import { getColor } from "../../lib/helper";
import { TCanvasNode } from "../types/TCanvasNode";
import { TCanvasOptions } from "../types/TCanvasOptions";
import getNodeSize, { fitString, getTextLimit, getTextOffset, getTextSize } from "./getNodeSize";

const THEME_TEXT_PRIMARY = "#FFFFFF";
const THEME_TEXT_SECONDARY = "#A3AED0";
const THEME_NODE_DEFAULT = "#17203C";
const THEME_NODE_BORDER = "#0099FF";
const THEME_ACCENT = "#0099FF";

export default function drawNodes(
  node: TCanvasNode,
  ctx: CanvasRenderingContext2D,
  scale: number,
  imgs: Record<string, HTMLImageElement>,
  canvasOptions: TCanvasOptions | null,
  anyHovering: boolean,
  transition: number | null,
  highlightedNodeIds?: number[]
) {
  // Check if this node should be highlighted for data product builder
  const isDataProductHighlighted = highlightedNodeIds && highlightedNodeIds.length > 0 
    ? highlightedNodeIds.includes(node.id)
    : null;

  if (node.node_type != 'OTHER') {
    let textHighlight: boolean | null = node.name.toLowerCase().includes(canvasOptions?.searchText.toLowerCase() ?? "")
    textHighlight = canvasOptions?.searchText.trim() == "" ? null : textHighlight
    const shouldNodeHighlight = node.highlight ?? true
    const options = {transition}
    
    // Data product highlighting overrides other highlighting when active
    if (isDataProductHighlighted !== null) {
      drawNode(node, ctx, scale, imgs, options, isDataProductHighlighted, canvasOptions?.showDQ ?? false);
    } else if (anyHovering) {
      if (node.hovered) {
        drawNode(node, ctx, scale, imgs, options, true, canvasOptions?.showDQ ?? false);
      } else {
        drawNode(node, ctx, scale, imgs, options, false, false);
      }
    } else {
      drawNode(node, ctx, scale, imgs, options, shouldNodeHighlight, canvasOptions?.showDQ ?? false);
    }
  } else {
    if (node.payload) {
      let textHighlight: boolean | null = null
      for (let i = 0; i < node.payload.length; i++) {
        if (node.payload[i].name.toLowerCase().includes(canvasOptions?.searchText.toLowerCase())) {
          textHighlight = true;
          break;
        }
      }
      textHighlight = canvasOptions?.searchText.trim() == "" ? null : textHighlight
      const shouldNodeHighlight = canvasOptions?.searchDisplayMode == "filter" ? null : textHighlight
      const options = {transition}
      
      // Data product highlighting overrides
      if (isDataProductHighlighted !== null) {
        drawNode(node, ctx, scale, imgs, options, isDataProductHighlighted, canvasOptions?.showDQ ?? false);
      } else if (anyHovering) {
        if (node.hovered) {
          drawNode(node, ctx, scale, imgs, options, true, canvasOptions?.showDQ ?? false);
        } else {
          drawNode(node, ctx, scale, imgs, options, false, canvasOptions?.showDQ ?? false);
        }
      } else {
        drawNode(node, ctx, scale, imgs, options, shouldNodeHighlight, canvasOptions?.showDQ ?? false);
      }
    }
  }
}

const drawNode = (
  node: TCanvasNode,
  ctx: CanvasRenderingContext2D,
  scale: number,
  imgs: Record<string, HTMLImageElement>,
  options: Record<string, any>,
  highlight?: boolean | null,
  showDQ?: boolean
) => {

  if (node.x == undefined || node.y == undefined) return
  if (node.node_type == "DEFAULT_DOMAIN") return
  
  // Apply reduced opacity for non-highlighted nodes
  if (highlight == false) {
    ctx.globalAlpha = 0.18;
  }
  
  if (node.node_type != 'OTHER') {
    let nodeColor = node.color ?? THEME_NODE_DEFAULT
    
    // In dark mode, we want vibrant nodes
    if (highlight) {
        nodeColor = node.color ?? THEME_ACCENT;
    } else if (highlight == false) {
        // Dimmed state with darker color
        nodeColor = "#1A2840"; 
    }

    if ((highlight == undefined || highlight) && node.payload && node.payload.length > 0 && node.payload[0].profile && node.node_type == "COLUMN" && showDQ) {
      const complete = node.payload[0].profile[3];
      const validEndAngle = complete * 2 * Math.PI;
      
      // Outer border for DQ - same styling as regular nodes
      ctx.beginPath()
      ctx.globalAlpha = 0.3
      ctx.fillStyle = nodeColor
      ctx.arc(node.x, node.y, node.hovered ? getNodeSize(node) + 5.5 : getNodeSize(node) + 3.1, 0, 2 * Math.PI, false);
      ctx.fill()
      // Restore the fading alpha if node should be faded, otherwise reset to 1.0
      ctx.globalAlpha = highlight == false ? 0.18 : 1.0
      
      // DQ visualization with green/red on bright border layer
      ctx.fillStyle = '#00b050'
      ctx.beginPath()
      ctx.moveTo(node.x ?? 0, node.y ?? 0)
      ctx.arc(node.x, node.y, node.hovered ? getNodeSize(node) + 1.5 : getNodeSize(node) + 0.1, 0, validEndAngle, false);
      ctx.lineTo(node.x, node.y)
      ctx.fill()
      ctx.fillStyle = '#ff4140' // Keeping red for error/incomplete
      ctx.beginPath()
      ctx.moveTo(node.x ?? 0, node.y ?? 0)
      ctx.arc(node.x, node.y, node.hovered ? getNodeSize(node) + 1.5 : getNodeSize(node) + 0.1, validEndAngle, 2 * Math.PI, false);
      ctx.lineTo(node.x ?? 0, node.y ?? 0)
      ctx.fill()
    } else {

      ctx.fillStyle = nodeColor
      // Glow effect for highlighted nodes
      if (highlight) {
          ctx.shadowColor = nodeColor;
          ctx.shadowBlur = 15;
      }
      
      // Outer border - 30% opacity
      ctx.beginPath()
      ctx.globalAlpha = 0.3 // 30% opacity (70% transparent)
      ctx.arc(node.x, node.y, node.hovered ? getNodeSize(node) + 5.5 : getNodeSize(node) + 3.1, 0, 2 * Math.PI, false);
      ctx.fill()
      // Restore the fading alpha if node should be faded, otherwise reset to 1.0
      ctx.globalAlpha = highlight == false ? 0.18 : 1.0
      ctx.shadowBlur = 0; // Reset shadow

      // Inner bright border - 30% thinner (reduced from 3px to 2.1px normal, 5px to 3.5px hovered)
      ctx.beginPath()
      ctx.arc(node.x, node.y, node.hovered ? getNodeSize(node) + 1.5 : getNodeSize(node) + 0.1, 0, 2 * Math.PI, false)
      ctx.fill()
    }

    // Inner circle (blue-toned background to match theme) - should also expand on hover
    ctx.fillStyle = '#0f172a' // slate-900 matching canvas background
    ctx.beginPath()
    ctx.arc(node.x, node.y, node.hovered ? getNodeSize(node) : getNodeSize(node) - 2, 0, 2 * Math.PI, false);
    ctx.fill()

    // draw images
    // Alpha already set at beginning if highlight == false (0.18 for faded icons)
    const iconSize = getNodeSize(node) - 2;
    
    // Icons are already theme-colored (#94A3B8), no filter needed
    if (node.node_type == 'COLUMN') {
      if (scale > 0.7) {
        const payload = node.payload ? node.payload[0] : undefined
        payload && imgs[payload.datatype] && ctx.drawImage(imgs[payload.datatype ?? node.node_type], node.x - iconSize / 2, node.y - iconSize / 2, iconSize, iconSize)
      }
    } else {
      imgs[node.node_type] && ctx.drawImage(imgs[node.node_type], node.x - iconSize / 2, node.y - iconSize / 2, iconSize, iconSize)
    }
    // ctx.fillStyle = "white"
  } else {
    // Other nodes - applying same border styling as DOMAIN/TABLE/COLUMN
    let nodeColor = THEME_TEXT_SECONDARY
    if (highlight == true) nodeColor = THEME_TEXT_PRIMARY
    else if (highlight == false) nodeColor = "#1A2840" // Darker color for faded appearance
    
    ctx.fillStyle = nodeColor
    
    // Glow effect for highlighted nodes
    if (highlight) {
        ctx.shadowColor = nodeColor;
        ctx.shadowBlur = 15;
    }
    
    // Outer border - 30% opacity
    ctx.beginPath()
    ctx.globalAlpha = 0.3 // 30% opacity (70% transparent)
    ctx.arc(node.x, node.y, node.hovered ? getNodeSize(node) + 5.5 : getNodeSize(node) + 3.1, 0, 2 * Math.PI, false);
    ctx.fill()
    ctx.globalAlpha = 1.0
    ctx.shadowBlur = 0; // Reset shadow

    // Inner bright border - 30% thinner
    ctx.beginPath()
    ctx.arc(node.x, node.y, node.hovered ? getNodeSize(node) + 1.5 : getNodeSize(node) + 0.1, 0, 2 * Math.PI, false)
    ctx.fill()
    
    // Inner circle background - should also expand on hover
    ctx.fillStyle = "#0f172a" // slate-900 matching canvas background
    ctx.beginPath()
    ctx.arc(node.x, node.y, node.hovered ? getNodeSize(node) : getNodeSize(node) - 2, 0, 2 * Math.PI, false)
    ctx.fill()
  }

  // TEXT RENDERING
  ctx.fillStyle = THEME_TEXT_PRIMARY
  if (node.node_type == 'DOMAIN') {
    const textColor = highlight == false ? "rgba(255, 255, 255, 0.18)" : THEME_TEXT_PRIMARY;
    writeText(node, ctx, node.name, node.x, node.y + getTextOffset(node) + (getTextOffset(node)/scale*0.5), Math.max(Math.min(getTextSize(node)/scale, 50), 30), 'Sans-Serif', textColor, 600)
  } else if (node.node_type == "TABLE") {
    const textColor = highlight == false ? "rgba(255, 255, 255, 0.18)" : THEME_TEXT_PRIMARY;
    writeText(node, ctx, node.name, node.x, node.y + getTextOffset(node),getTextSize(node), 'Sans-Serif', textColor, 600)
  } else if (node.node_type == "COLUMN") {
    // Single text rendering for COLUMN - adjust size and position based on hover
    if(node.hovered || scale > 0.4) {
      const textColor = node.hovered ? THEME_TEXT_PRIMARY : (highlight == false ? "rgba(163, 174, 208, 0.18)" : THEME_TEXT_SECONDARY);
      const yOffset = node.hovered ? 10 : 3;
      const sizeIncrease = node.hovered ? 10 : 3;
      const fontWeight = node.hovered ? 600 : 200;
      writeText(node, ctx, node.name, node.x, node.y + getTextOffset(node) + yOffset, getTextSize(node) + sizeIncrease, 'Sans-Serif', textColor, fontWeight, node.hovered ? 400 : undefined)
    }
  }

  // Reset alpha after all drawing
  if (highlight == false) {
    ctx.globalAlpha = 1.0;
  }
}


function writeText(
  node: TCanvasNode,
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  fontSize: number,
  fontFamily: string,
  color: string,
  fontWeight?: number,
  limit?: number
) {
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.miterLimit = 1;
  
  const fittedText = fitString(ctx, text, limit ?? getTextLimit(node))
  
  // Stroke for contrast against complex backgrounds
  ctx.lineWidth = 3
  ctx.strokeStyle = "#0A1126" // Dark stroke around text
  ctx.strokeText(fittedText, x, y)
  
  ctx.fillStyle = color
  ctx.fillText(fittedText, x, y)
}
